import React from "react"
import { Route, Routes, Navigate, useLocation } from "react-router"
import SidebarContainer from "../../Sidebar/SidebarContainer"
import SidebarContent from "../../Sidebar/Content"
import ClassSidebar from "../General/ClassSidebar"
import DashboardAuth from "../General/Auth/DashboardAuth"
import student from "shared/routes/student"
import { ClassResponse } from "shared/types/studentTypes"
import refreshClassesFactory from "../General/Helpers/refreshClassesFactory"

import StudentOverview from "./Overview"
import StudentAssignments from "./StudentAssignments"
import StudentAssignment, { StudentAssignmentState } from "./StudentAssignment"
import StudentGrades from "./StudentGrades"
import Submissions from "./Submissions"
import StudentSubmission, { SubmissionStudentState } from "pages/Dashboard/Student/Grades/Submission/SubmissionStudent"
import checkRouteState from "helpers/checkRouteState"
import { faHome, faTasks, faBook, faBell, faQuestionCircle } from "@fortawesome/free-solid-svg-icons"
import RelevantHelpModal from "components/Modals/RelevantHelpModal"
import StudentNotificationsCenter from "./Notifications/StudentNotificationsCenter"
import StudentJeopardy from "./StudentJeopardy/StudentJeopardy"
import { AuthContext } from "AuthContext"


export default function StudentDashboard() {
    const location = useLocation()
    const [classes, setClasses] = React.useState<ClassResponse[]>([])
    const [activeClass, setActiveClass] = React.useState<ClassResponse>(null)
    const [loading, setLoading] = React.useState(true)
    const { hasStemSimsAccess, hasCurriculumAccess, scienceBlocks } = React.useContext(AuthContext)

    const refreshClasses = refreshClassesFactory({
        getClasses: student.getClasses,
        setLoading,
        setClasses,
        setActiveClass
    })

    const validateGradingRoute = (routeStateClass, Component, url="/dashboard/student/overview") => {
        const validStateSubmission = checkRouteState(location?.state, routeStateClass)
        if(validStateSubmission) {
            location.state = validStateSubmission
            return Component
        }
        return <Navigate to={url} /> 
    }

    const validateAccess = ({ hasAccess, Component, redirectUrl } : { hasAccess: boolean, Component: React.ReactNode, redirectUrl: string }) => {
        if (hasAccess) {
            return Component
        }
        return <Navigate to={redirectUrl} /> 
    }



    // Component did mounted
    React.useEffect(() => {
        void refreshClasses()
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [])

    // Cache active class
    React.useEffect(() => {
        if (activeClass) {
            localStorage.setItem("activeClass", JSON.stringify(activeClass))
        }
    }, [activeClass])

    const sidebarLinks = !(hasStemSimsAccess || hasCurriculumAccess) ? [] : [
        {
            title: "Overview",
            path: "/dashboard/student/overview",
            icon: faHome,
            available: true,
        },
        {
            title: "Assignments",
            path: "/dashboard/student/assignments",
            icon: faTasks,
            available: true,
        },
        {
            title: "Grades",
            path: "/dashboard/student/grades",
            icon: faBook,
            available: true,
        },
        {
            title: "Core Ideas",
            path: "/dashboard/student/reviews",
            icon: faQuestionCircle,
            available: scienceBlocks.length > 0,
        },
        {
            title: "Notifications",
            path: "/dashboard/student/notifications",
            icon: faBell,
            available: true,
        }
    ]

    return (
        <SidebarContainer>
            <RelevantHelpModal videos={[]} />
            <ClassSidebar
                    activeClass={activeClass}
                    classes={classes}
                    onClassSelect={(selectedClass) => {
                        setActiveClass(selectedClass)
                    }}
                    linkGroups={[
                        {
                            header: null,
                            links: sidebarLinks,
                        }
                    ]}
                    isStudent
                />
            
            <SidebarContent>
                <DashboardAuth
                    activeClass={activeClass}
                    loading={loading}
                    redirectUrl="/dashboard/student/overview"
                    permission="STUDENT"
                >
                    <Routes>
                        <Route
                            path="/overview"
                            element={
                                validateAccess({ hasAccess: hasStemSimsAccess || hasCurriculumAccess, Component: <StudentOverview activeClass={activeClass} />, redirectUrl: "/" })
                            }
                        />

                        <Route
                            path="/assignments"
                            element={
                                validateAccess({ hasAccess: hasStemSimsAccess || hasCurriculumAccess, Component: <StudentAssignments activeClass={activeClass} />, redirectUrl: "/" })
                            }
                        />

                        <Route
                            path="/assignments/assignment"
                            element={
                                validateGradingRoute(StudentAssignmentState, <StudentAssignment />, "dashboard/student/assignments")
                            }
                        />

                        <Route
                            path="/grades"
                            element={
                                validateAccess({ hasAccess: hasStemSimsAccess || hasCurriculumAccess, Component: <StudentGrades activeClass={activeClass} />, redirectUrl: "/" })
                            }
                        />

                        <Route
                            path="/grades/submissions"
                            element={
                                validateAccess({ hasAccess: hasStemSimsAccess || hasCurriculumAccess, Component: <Submissions />, redirectUrl: "/" })
                            }
                        />
                        <Route
                            path="/grades/submission"
                            element={
                                validateGradingRoute(SubmissionStudentState, <StudentSubmission />, "dashboard/student/grades")
                            }
                        />
                        <Route
                            path="/reviews"
                            element={
                                validateAccess({ hasAccess: scienceBlocks.length > 0, Component: <StudentJeopardy activeClass={activeClass} />, redirectUrl: "/dashboard/student/overview" })
                            }
                        />
                        <Route
                            path="/reviews/:assignmentId"
                            element={
                                validateAccess({ hasAccess: scienceBlocks.length > 0, Component: <StudentJeopardy activeClass={activeClass} />, redirectUrl: "/dashboard/student/overview" })
                            }
                        />
                        <Route path="*" element={<Navigate to="/dashboard/student/overview" replace={true} />} />
                        <Route path="/notifications" element={<StudentNotificationsCenter />} />
                    </Routes>
                </DashboardAuth>
            </SidebarContent>
        </SidebarContainer>
    )
}
